// src/pages/agent-booking-system-sabre-integration/components/BookingQueue.jsx
import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const BookingQueue = ({ queue, onItemSelect, selectedItem, userProfile, onRefresh }) => {
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('priority');

  const priorityColors = {
    1: 'bg-red-100 text-red-800 border-red-200',
    2: 'bg-orange-100 text-orange-800 border-orange-200',
    3: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    4: 'bg-blue-100 text-blue-800 border-blue-200',
    5: 'bg-gray-100 text-gray-800 border-gray-200'
  };

  const priorityLabels = {
    1: 'URGENT',
    2: 'HIGH',
    3: 'MEDIUM',
    4: 'NORMAL',
    5: 'LOW'
  };

  const filteredQueue = queue?.filter(item => {
    if (filter === 'all') return true;
    if (filter === 'urgent') return item?.priority_level <= 2;
    if (filter === 'overdue') {
      return new Date(item.sla_deadline) < new Date();
    }
    return true;
  }) || [];

  const sortedQueue = filteredQueue?.sort((a, b) => {
    switch (sortBy) {
      case 'priority':
        return a?.priority_level - b?.priority_level;
      case 'deadline':
        return new Date(a.sla_deadline) - new Date(b.sla_deadline);
      case 'assigned':
        return new Date(b.assigned_at) - new Date(a.assigned_at);
      default:
        return 0;
    }
  });

  const getTimeRemaining = (deadline) => {
    const now = new Date();
    const slaTime = new Date(deadline);
    const diff = slaTime - now;
    
    if (diff <= 0) {
      return { text: 'OVERDUE', isOverdue: true };
    }
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return { text: `${days}d ${hours % 24}h`, isOverdue: false };
    }
    
    return { text: `${hours}h ${minutes}m`, isOverdue: false };
  };

  const handleMarkCompleted = (queueItem) => {
    // In real app, this would call an API to mark the item as completed
    console.log('Marking completed:', queueItem);
    onRefresh?.();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-heading-bold text-text-primary mb-2">
            Booking Queue
          </h1>
          <p className="text-text-secondary">
            Manage your assigned bookings and monitor SLA compliance
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          <button
            onClick={onRefresh}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-body-medium text-text-secondary border border-border rounded-lg hover:bg-secondary-50 transition-colors"
          >
            <Icon name="RefreshCw" size={16} />
            <span>Refresh</span>
          </button>
          
          <div className="flex items-center space-x-2">
            <span className="text-sm text-text-secondary">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e?.target?.value)}
              className="px-3 py-1 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="priority">Priority</option>
              <option value="deadline">SLA Deadline</option>
              <option value="assigned">Recently Assigned</option>
            </select>
          </div>
        </div>
      </div>
      {/* Stats Bar */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon name="Clock" size={20} className="text-blue-600" />
            </div>
            <div>
              <div className="text-lg font-heading-semibold text-text-primary">
                {queue?.length || 0}
              </div>
              <div className="text-sm text-text-secondary">Total Queue</div>
            </div>
          </div>
        </div>

        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} className="text-red-600" />
            </div>
            <div>
              <div className="text-lg font-heading-semibold text-text-primary">
                {filteredQueue?.filter(item => item?.priority_level <= 2)?.length}
              </div>
              <div className="text-sm text-text-secondary">High Priority</div>
            </div>
          </div>
        </div>

        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Icon name="Clock" size={20} className="text-orange-600" />
            </div>
            <div>
              <div className="text-lg font-heading-semibold text-text-primary">
                {filteredQueue?.filter(item => {
                  const remaining = getTimeRemaining(item?.sla_deadline);
                  return remaining?.isOverdue;
                })?.length}
              </div>
              <div className="text-sm text-text-secondary">Overdue</div>
            </div>
          </div>
        </div>

        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Icon name="TrendingUp" size={20} className="text-green-600" />
            </div>
            <div>
              <div className="text-lg font-heading-semibold text-text-primary">
                92%
              </div>
              <div className="text-sm text-text-secondary">SLA Compliance</div>
            </div>
          </div>
        </div>
      </div>
      {/* Filter Tabs */}
      <div className="flex space-x-1 mb-6">
        {[
          { id: 'all', label: 'All Items', count: queue?.length || 0 },
          { id: 'urgent', label: 'Urgent', count: filteredQueue?.filter(item => item?.priority_level <= 2)?.length },
          { id: 'overdue', label: 'Overdue', count: filteredQueue?.filter(item => getTimeRemaining(item?.sla_deadline)?.isOverdue)?.length }
        ]?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setFilter(tab?.id)}
            className={`flex items-center space-x-2 px-4 py-2 text-sm font-body-medium rounded-lg transition-colors ${
              filter === tab?.id
                ? 'bg-primary-50 text-primary-700 border border-primary-200' :'text-text-secondary hover:bg-secondary-50 hover:text-text-primary'
            }`}
          >
            <span>{tab?.label}</span>
            {tab?.count > 0 && (
              <span className="px-1.5 py-0.5 bg-text-secondary bg-opacity-20 text-xs rounded-full">
                {tab?.count}
              </span>
            )}
          </button>
        ))}
      </div>
      {/* Queue Items */}
      <div className="space-y-4">
        {sortedQueue?.length > 0 ? sortedQueue?.map((queueItem) => {
          const booking = queueItem?.booking;
          const timeRemaining = getTimeRemaining(queueItem?.sla_deadline);
          
          return (
            <div
              key={queueItem?.id}
              className={`bg-surface border rounded-lg p-6 transition-all cursor-pointer ${
                selectedItem?.id === queueItem?.id
                  ? 'border-primary-200 shadow-md'
                  : 'border-border hover:shadow-sm'
              } ${timeRemaining?.isOverdue ? 'border-l-4 border-l-red-500' : ''}`}
              onClick={() => onItemSelect?.(queueItem)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Priority Badge */}
                  <div className="flex items-center space-x-3 mb-3">
                    <span className={`px-2 py-1 text-xs font-body-bold rounded-full border ${
                      priorityColors?.[queueItem?.priority_level] || priorityColors?.[5]
                    }`}>
                      {priorityLabels?.[queueItem?.priority_level] || 'LOW'}
                    </span>
                    
                    <span className="text-sm font-body-medium text-text-primary">
                      {booking?.booking_reference || `BK-${booking?.id?.slice(-6)}`}
                    </span>
                    
                    <span className={`text-sm font-body-medium px-2 py-1 rounded-full ${
                      timeRemaining?.isOverdue 
                        ? 'bg-red-100 text-red-800' :'bg-green-100 text-green-800'
                    }`}>
                      {timeRemaining?.isOverdue ? 'OVERDUE' : timeRemaining?.text}
                    </span>
                  </div>

                  {/* Booking Details */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <h3 className="font-heading-semibold text-text-primary mb-1">
                        {booking?.traveler?.full_name || 'Unknown Traveler'}
                      </h3>
                      <p className="text-sm text-text-secondary">
                        {booking?.traveler?.email}
                      </p>
                    </div>
                    
                    <div>
                      <div className="text-sm text-text-secondary mb-1">Booking Type</div>
                      <div className="font-body-medium text-text-primary capitalize">
                        {booking?.booking_type || 'Mixed'}
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-sm text-text-secondary mb-1">Total Value</div>
                      <div className="font-body-medium text-text-primary">
                        ${booking?.total_cost?.toLocaleString() || '0'}
                      </div>
                    </div>
                  </div>

                  {/* Trip Information */}
                  {booking?.trip_request && (
                    <div className="mb-4 p-3 bg-secondary-50 rounded-lg">
                      <div className="text-sm font-body-medium text-text-primary mb-1">
                        {booking?.trip_request?.title}
                      </div>
                      <div className="text-xs text-text-secondary">
                        Purpose: {booking?.trip_request?.purpose || 'Business travel'}
                      </div>
                    </div>
                  )}

                  {/* Action Items */}
                  <div className="text-sm text-text-secondary">
                    <div className="flex items-center space-x-1 mb-1">
                      <Icon name="Clock" size={14} />
                      <span>Assigned {new Date(queueItem.assigned_at)?.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Target" size={14} />
                      <span>SLA Deadline: {new Date(queueItem.sla_deadline)?.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col space-y-2 ml-6">
                  <button
                    onClick={(e) => {
                      e?.stopPropagation();
                      onItemSelect?.(queueItem);
                    }}
                    className="px-4 py-2 bg-primary-600 text-white text-sm font-body-medium rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Process
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e?.stopPropagation();
                      handleMarkCompleted(queueItem);
                    }}
                    className="px-4 py-2 bg-green-600 text-white text-sm font-body-medium rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Complete
                  </button>
                  
                  <button className="px-4 py-2 text-sm font-body-medium text-text-secondary border border-border rounded-lg hover:bg-secondary-50 transition-colors">
                    Details
                  </button>
                </div>
              </div>
            </div>
          );
        }) : (
          <div className="text-center py-12">
            <Icon name="CheckCircle" size={48} className="text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-heading-semibold text-text-primary mb-2">
              Queue is empty!
            </h3>
            <p className="text-text-secondary">
              {filter === 'all' ?'No bookings in your queue right now.'
                : `No ${filter} items found.`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingQueue;